import {
  useCallback,
  useState,
  SetStateAction,
  Dispatch,
  useMemo,
  useEffect,
  MouseEventHandler,
  ComponentProps,
} from 'react';
import styled from 'styled-components';
import useMuiTableBuilderV2, { ListItem } from 'hooks/useMuiTableBuilderV2';
import { useAppSelector } from 'redux/hooks';
import { Box, Button, TablePagination } from '@mui/material';
import { Realm } from 'types';
import {
  formatActiveRealms,
  formatNonActiveRealms,
  RealmListItem,
  SandboxTableConfig,
} from './sharedTableConfigs';
import { selectRailsSandboxesTableFilter } from 'SandboxModule/selectors';
import { ToggleOptionsButton } from 'components/ToggleOptionsButton';
import orderBy from 'lodash/orderBy';
import SandboxFiltersContainer from 'SandboxModule/components/SandboxFiltersContainer';
import useSandboxes, { SandboxIsoStateIds } from 'SandboxModule/hooks/useSandboxes';
import { REALM_TABLE_VIEW_ARRAY } from 'RealmModule/constants/realm';
import { useTenants } from 'TenantModule/hooks/useTenants';
import { useTablePagination } from 'hooks/useTablePagination';
import { RealmTableView, RealmTypes } from 'RealmModule/constants/realm';
import {
  getRealmTableV2Config,
  ROW_TYPES,
} from 'SandboxModule/components/RealmTableV2/tableConfigs';
import type { RealmRowData } from 'SandboxModule/components/RealmTableV2/utils';
import useRequestSort from 'hooks/useRequestSort';
import useExportToExcel from 'hooks/useExportToExcel';
import { Download } from 'lucide-react';

type SandboxesTableProps = {
  tableConfig: SandboxTableConfig;
  sandboxTableView: RealmTableView;
  setSandboxTableView: Dispatch<SetStateAction<RealmTableView>>;
  onModifyButtonClick: MouseEventHandler<HTMLElement>;
  checkedSandboxes: Set<string>;
  setCheckedSandboxesSet: Dispatch<SetStateAction<Set<string>>>;
  realmType: string;
  showFilter?: boolean;
  /**
   * Client-side text filter matching realm ID OR team name.
   * Applied on the frontend because GET /realm/all combines its two text
   * params (realmIdSearchKey, teamNameSearchKey) with AND.
   */
  searchText?: string;
  /**
   * Incremented by the page on every search/filter submission so the table
   * returns to the first page, matching the Teams/Members behavior
   */
  filtersSubmitCount?: number;
};

const SandboxTable = ({
  tableConfig,
  sandboxTableView,
  setSandboxTableView,
  onModifyButtonClick,
  checkedSandboxes,
  setCheckedSandboxesSet,
  realmType,
  showFilter = true,
  searchText,
  filtersSubmitCount,
}: SandboxesTableProps) => {
  const { fetchSandboxes, getSandboxesByIsoState, getSandbox } = useSandboxes();
  const { selectedGlobalTenant } = useTenants();
  const filter = useAppSelector(selectRailsSandboxesTableFilter);
  const { isRequesting: _isFetchingSandboxes, ordersByGroup } = getSandboxesByIsoState(
    SandboxIsoStateIds.SandboxTable,
  );

  // Track if initial fetch has occurred to prevent refetch loops
  const [hasInitiallyFetched, setHasInitiallyFetched] = useState(false);
  // Stable loading indicator - only show on first load
  const [_isInitialLoad, setIsInitialLoad] = useState(true);

  const triggerSandboxFetches = useCallback(() => {
    fetchSandboxes(SandboxIsoStateIds.SandboxTable);
    fetchSandboxes(SandboxIsoStateIds.SandboxCreationTimer);
    setHasInitiallyFetched(true);
    setIsInitialLoad(false);
  }, [fetchSandboxes]);

  // Only disable bulk menu based on selection, not fetch status
  // This prevents button state changes during background updates
  const disableBulkMenu = !checkedSandboxes.size;

  const onViewToggle: ComponentProps<typeof ToggleOptionsButton>['onSelect'] = useCallback(
    ({
      option,
    }: Parameters<NonNullable<ComponentProps<typeof ToggleOptionsButton>['onSelect']>>[0]) => {
      setSandboxTableView(option.value as RealmTableView);
    },
    [setSandboxTableView],
  );

  // Fetch data only once on mount, not on every dependency change
  useEffect(() => {
    if (!hasInitiallyFetched) {
      triggerSandboxFetches();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Currently sort on FE
  const realmListItems: RealmListItem[] = useMemo(() => {
    /**
     * Spec
     * - Sandbox table should only show Sandbox realm types
     */
    const order = ordersByGroup[realmType];
    const normalizedSearchText = searchText?.trim().toLowerCase();

    const realmsArray = order?.reduce<Realm[]>((acc, id) => {
      const realm = getSandbox(id);

      const currentTenant = selectedGlobalTenant;

      if (realm && currentTenant) {
        const isDefaultTenant = currentTenant.isDefault;

        const isValidRealm =
          // For default tenant, always show all realms
          isDefaultTenant ||
          // For non-default tenant, only show the realms on that tenant
          (!isDefaultTenant && realm.tenant === currentTenant.tenantName);

        const matchesSearchText =
          !normalizedSearchText ||
          realm.realm_id.toLowerCase().includes(normalizedSearchText) ||
          (realm.team_name || '').toLowerCase().includes(normalizedSearchText);

        if (isValidRealm && matchesSearchText) {
          acc.push(realm);
        }
      }

      return acc;
    }, []);

    return orderBy(realmsArray, filter.orderBy, filter.orderDirection).map((realm) => ({
      ...realm,
      id: realm.realm_id,
      selected: false, // Will be set separately to avoid unnecessary re-renders
    }));
  }, [
    ordersByGroup,
    realmType,
    searchText,
    filter.orderBy,
    filter.orderDirection,
    getSandbox,
    selectedGlobalTenant,
  ]);

  const { startIndex, endIndex, pageNumber, handlePageChange, limit } = useTablePagination();

  // Full filtered list for the current view (pre-pagination); also used by
  // the Export button so exports cover every matching row, not just the page
  const viewRealmListItems = useMemo(
    () =>
      sandboxTableView === RealmTableView.Active
        ? formatActiveRealms({ data: realmListItems })
        : formatNonActiveRealms({ data: realmListItems }),
    [realmListItems, sandboxTableView],
  );

  const { count, paginatedRealmListItems } = useMemo(() => {
    return {
      paginatedRealmListItems: viewRealmListItems.slice(startIndex, endIndex),
      count: viewRealmListItems.length,
    };
  }, [endIndex, viewRealmListItems, startIndex]);

  // Return to the first page on every search/filter submission, matching
  // the Teams/Members pages
  useEffect(() => {
    if (filtersSubmitCount) {
      handlePageChange({ newPageNumber: 0 });
    }
  }, [filtersSubmitCount, handlePageChange]);

  // Safety net: when the list shrinks below the current page (e.g. the
  // Active/Archived view toggle), jump back to the first page
  useEffect(() => {
    if (pageNumber > 0 && startIndex >= count) {
      handlePageChange({ newPageNumber: 0 });
    }
  }, [count, pageNumber, startIndex, handlePageChange]);

  // Add selected state separately to avoid re-computing the entire list on checkbox changes
  const paginatedRealmListItemsWithSelection = useMemo(() => {
    return paginatedRealmListItems.map((item) => ({
      ...item,
      selected: checkedSandboxes.has(item.id),
    }));
  }, [paginatedRealmListItems, checkedSandboxes]);

  const onPageChange = useCallback(
    (event: React.MouseEvent<HTMLButtonElement, MouseEvent> | null, page: number) => {
      handlePageChange({ newPageNumber: page });
    },
    [handlePageChange],
  );

  const paginationProps = useMemo(() => {
    return {
      onPageChange,
      count,
      page: pageNumber,
      rowsPerPage: limit,
      rowsPerPageOptions: [],
    };
  }, [onPageChange, count, pageNumber, limit]);

  const renderFooterOptions = useCallback(() => {
    return <TablePagination component="div" color="primary" {...paginationProps} />;
  }, [paginationProps]);

  // Build V2 list items
  const v2ListItems: ListItem[] = useMemo(() => {
    const formatDataFn = tableConfig?.formatData;
    const formatted = formatDataFn
      ? formatDataFn({ data: paginatedRealmListItemsWithSelection })
      : paginatedRealmListItemsWithSelection;

    const header: ListItem<object> = {
      id: ROW_TYPES.HEADER,
      rowType: ROW_TYPES.HEADER,
      disableRowHover: true,
      isStickyHeader: true,
    };

    const rows: ListItem<RealmRowData>[] = formatted.map((realm) => {
      const { selected: _selected, ...realmData } = realm; // drop local UI flag
      return {
        id: realm.id,
        rowType: ROW_TYPES.DATA,
        ...(realmData as RealmRowData),
      };
    });

    return [header, ...rows];
  }, [paginatedRealmListItemsWithSelection, tableConfig?.formatData]);

  const v2TableConfig = useMemo(
    () => getRealmTableV2Config({ variant: sandboxTableView }),
    [sandboxTableView],
  );
  // Sorting controls consistent with Teams/Members tables
  const { toggleSort } = useRequestSort({ filter });

  const tableHeaderTitle = useMemo(
    () => (realmType === RealmTypes.subscription ? 'Subscription' : 'Sandbox'),
    [realmType],
  );

  // Export the filtered result list (Teams pattern: save/download search results)
  const { exportToExcel } = useExportToExcel();
  const onExport = useCallback(() => {
    const rows = viewRealmListItems.map((item) => ({
      'Realm ID': item.realm_id,
      'Team Name': item.team_name ?? '',
      Tenant: item.tenant,
      Primary: item.is_primary ? 'Yes' : '',
      Status: item.status,
      'Created At': item.created_at ?? '',
      'Created By': item.created_by ?? '',
      Tags: (item.tags || []).join(', '),
      Services: (item.realm_services || []).join(', '),
      'Archived At': item.archived_at ?? '',
      'Archived By': item.archived_by ?? '',
    }));
    exportToExcel({
      data: rows,
      fileName: realmType === RealmTypes.subscription ? 'subscriptions' : 'sandboxes',
    });
  }, [exportToExcel, realmType, viewRealmListItems]);

  const onSort = useCallback((columnId: string) => toggleSort({ orderBy: columnId }), [toggleSort]);

  const renderHeaderLeftTools = useCallback(
    () => (
      <ToggleOptionsButton
        options={REALM_TABLE_VIEW_ARRAY}
        currentOption={sandboxTableView}
        onSelect={onViewToggle}
        isDisabled={false}
      />
    ),
    [sandboxTableView, onViewToggle],
  );

  const renderHeaderTools = useCallback(
    () => (
      <>
        {showFilter ? (
          <Box
            sx={{
              mr: 2,
            }}
          >
            <SandboxFiltersContainer />
          </Box>
        ) : null}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <Box style={{ marginRight: '10px' }}>
            <Button
              variant="outlined"
              color="primary"
              startIcon={<Download size={16} />}
              disabled={!viewRealmListItems.length}
              onClick={onExport}
            >
              Export
            </Button>
          </Box>
          <Box
            style={{
              marginRight: '10px',
              visibility: tableConfig.shouldHideBulkActionSelector ? 'hidden' : 'visible',
            }}
          >
            <Button
              variant="outlined"
              color="primary"
              disabled={disableBulkMenu}
              onClick={onModifyButtonClick}
            >
              Modify
              <Box
                component="span"
                sx={{ display: 'inline-block', minWidth: 20, textAlign: 'right', ml: 0.5 }}
              >
                {checkedSandboxes.size > 0 ? checkedSandboxes.size : ''}
              </Box>
            </Button>
          </Box>
        </Box>
      </>
    ),
    [
      showFilter,
      tableConfig.shouldHideBulkActionSelector,
      disableBulkMenu,
      onModifyButtonClick,
      checkedSandboxes.size,
      onExport,
      viewRealmListItems.length,
    ],
  );

  const { TableComponent: SandboxTable } = useMuiTableBuilderV2({
    ...v2TableConfig,
    hideToolbar: false,
    tableHeaderTitle,
    mainList: { listItems: v2ListItems },
    isLoading: !hasInitiallyFetched,
    _customRowProps: {
      setCheckedSandboxesSet,
      checkedSandboxes,
      orderBy: filter.orderBy,
      orderDirection: filter.orderDirection,
      onSort,
    },
    tableMaxHeight: '800px',
    renderHeaderLeftTools,
    renderHeaderTools,
    renderFooterOptions: renderFooterOptions,
  });

  return (
    <StyledBox>
      <Box>{SandboxTable}</Box>
    </StyledBox>
  );
};

export default SandboxTable;

const StyledBox = styled(Box)`
  .menu-button-container {
    background: transparent;
  }
`;
